import { Type, type Static } from "typebox";
import { ProductKeySchema } from "./identity.ts";

/** Canonical nav order across Platform + all product apps. */
export const SUITE_PRODUCT_ORDER = ["platform", "source", "codify", "function", "radius"] as const;
export type SuiteProductKey = (typeof SUITE_PRODUCT_ORDER)[number];

export const SuiteProductKeySchema = Type.Union([
  Type.Literal("platform"),
  Type.Literal("codify"),
  Type.Literal("source"),
  Type.Literal("radius"),
  Type.Literal("function"),
]);

export interface SuiteProductLaunchDestination {
  readonly key: SuiteProductKey;
  readonly launchUrl: string;
}

export function orderSuiteProducts<T extends { key: SuiteProductKey }>(products: T[]): T[] {
  const rank = new Map(SUITE_PRODUCT_ORDER.map((key, index) => [key, index]));
  return [...products].sort((left, right) => rank.get(left.key)! - rank.get(right.key)!);
}

/**
 * The browser context is derived from this server-side registry; it never
 * decides a product destination from a URL supplied by the browser.
 */
export function areSuiteProductLaunchUrlsRegistered(
  products: readonly SuiteProductLaunchDestination[],
  registry: ReadonlyMap<SuiteProductKey, Pick<SuiteProductLaunchDestination, "launchUrl">>,
): boolean {
  if (products.length !== SUITE_PRODUCT_ORDER.length || registry.size !== SUITE_PRODUCT_ORDER.length) {
    return false;
  }

  return SUITE_PRODUCT_ORDER.every((key, index) => {
    const product = products[index];
    const registered = registry.get(key);
    return product?.key === key && product.launchUrl === registered?.launchUrl;
  });
}

export const ProductRegistryEntrySchema = Type.Object({
  key: ProductKeySchema,
  label: Type.String({ minLength: 1, maxLength: 40 }),
  launchUrl: Type.String({ format: "uri" }),
  callbackUrl: Type.String({ format: "uri" }),
}, { additionalProperties: false });
export type ProductRegistryEntry = Static<typeof ProductRegistryEntrySchema>;
